import { sanitizeMessage } from '@/shared/utils/sanitize'
import { sanitizeEntityUrl } from '@/shared/utils/text-entities'

type InlineStyleFlags = {
  bold: boolean
  italic: boolean
  underline: boolean
  strikethrough: boolean
}

const SEMANTIC_TAG_MAP: Record<string, string> = {
  b: 'b',
  strong: 'b',
  i: 'i',
  em: 'i',
  u: 'u',
  s: 's',
  strike: 's',
  del: 's',
  code: 'code',
  pre: 'code',
}

const DISCARD_TAGS = new Set([
  'style',
  'script',
  'noscript',
  'template',
  'head',
  'meta',
  'link',
  'title',
  'xml',
  'colgroup',
  'col',
  'svg',
  'math',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
])

const BLOCK_UNWRAP_TAGS = new Set([
  'div',
  'p',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'ul',
  'ol',
  'section',
  'article',
])

const TABLE_SECTION_TAGS = new Set(['table', 'thead', 'tbody', 'tfoot'])

const parseInlineStyleFlags = (styleAttr: string | null): InlineStyleFlags => {
  const flags: InlineStyleFlags = {
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
  }

  if (!styleAttr) return flags

  const normalized = styleAttr.toLowerCase()

  if (/font-weight\s*:\s*(bold|[7-9]00)/.test(normalized)) {
    flags.bold = true
  }
  if (/font-style\s*:\s*(italic|oblique)/.test(normalized)) {
    flags.italic = true
  }
  if (/text-decoration(?:-line)?\s*:[^;]*underline/.test(normalized)) {
    flags.underline = true
  }
  if (/text-decoration(?:-line)?\s*:[^;]*line-through/.test(normalized)) {
    flags.strikethrough = true
  }

  return flags
}

const wrapNodes = (doc: Document, tagName: string, nodes: Node[]): Node[] => {
  if (!nodes.length) return []

  const element = doc.createElement(tagName)
  for (const node of nodes) {
    element.appendChild(node)
  }

  return [element]
}

const applyInlineStyleWrappers = (
  doc: Document,
  nodes: Node[],
  flags: InlineStyleFlags,
): Node[] => {
  let wrapped = nodes

  if (flags.bold) wrapped = wrapNodes(doc, 'b', wrapped)
  if (flags.italic) wrapped = wrapNodes(doc, 'i', wrapped)
  if (flags.underline) wrapped = wrapNodes(doc, 'u', wrapped)
  if (flags.strikethrough) wrapped = wrapNodes(doc, 's', wrapped)

  return wrapped
}

const joinNodeGroups = (
  groups: Node[][],
  createSeparator: () => Node,
): Node[] => {
  const result: Node[] = []

  groups.forEach((group, index) => {
    if (index > 0) result.push(createSeparator())
    result.push(...group)
  })

  return result
}

const normalizeTableRow = (row: HTMLElement, doc: Document): Node[] => {
  const cells = Array.from(row.children).filter((child) => {
    const childTag = child.tagName.toLowerCase()
    return childTag === 'td' || childTag === 'th'
  })

  const cellGroups = cells.map((cell) => (
    Array.from(cell.childNodes).flatMap((child) => normalizeNode(child, doc))
  ))

  return joinNodeGroups(cellGroups, () => doc.createTextNode('\t'))
}

const normalizeTableSection = (element: HTMLElement, doc: Document): Node[] => {
  const rows: Node[][] = []

  const collectRows = (el: HTMLElement) => {
    for (const child of Array.from(el.children)) {
      const htmlChild = child as HTMLElement
      const childTag = htmlChild.tagName.toLowerCase()
      if (childTag === 'tr') {
        rows.push(normalizeTableRow(htmlChild, doc))
        continue
      }
      if (TABLE_SECTION_TAGS.has(childTag) || childTag === 'table') {
        collectRows(htmlChild)
      }
    }
  }

  collectRows(element)

  return joinNodeGroups(rows, () => doc.createElement('br'))
}

const normalizeNode = (node: Node, doc: Document): Node[] => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? ''
    return text ? [doc.createTextNode(text)] : []
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return []

  const element = node as HTMLElement
  const tag = element.tagName.toLowerCase()

  if (DISCARD_TAGS.has(tag)) return []

  if (tag === 'br') {
    return [doc.createElement('br')]
  }

  if (tag === 'a') {
    const href = element.getAttribute('href')
    const url = href ? sanitizeEntityUrl(href) : undefined
    const children = Array.from(element.childNodes).flatMap((child) => normalizeNode(child, doc))

    if (!url || !children.length) return children

    const anchor = doc.createElement('a')
    anchor.setAttribute('href', url)
    anchor.className = 'text-link'

    for (const child of children) {
      anchor.appendChild(child)
    }

    return [anchor]
  }

  const semanticTag = SEMANTIC_TAG_MAP[tag]
  if (semanticTag) {
    const children = Array.from(element.childNodes).flatMap((child) => normalizeNode(child, doc))
    return wrapNodes(doc, semanticTag, children)
  }

  if (tag === 'span' || tag === 'font') {
    const children = Array.from(element.childNodes).flatMap((child) => normalizeNode(child, doc))
    const flags = parseInlineStyleFlags(element.getAttribute('style'))

    if (tag === 'font') {
      const color = element.getAttribute('color')
      if (color) {
        // Ignore legacy font color; keep nested formatting only.
      }
    }

    return applyInlineStyleWrappers(doc, children, flags)
  }

  if (tag === 'table' || TABLE_SECTION_TAGS.has(tag)) {
    return normalizeTableSection(element, doc)
  }

  if (tag === 'tr') {
    return normalizeTableRow(element, doc)
  }

  if (tag === 'td' || tag === 'th') {
    return Array.from(element.childNodes).flatMap((child) => normalizeNode(child, doc))
  }

  if (BLOCK_UNWRAP_TAGS.has(tag)) {
    return Array.from(element.childNodes).flatMap((child) => normalizeNode(child, doc))
  }

  return Array.from(element.childNodes).flatMap((child) => normalizeNode(child, doc))
}

export const normalizePasteHtml = (html: string): string => {
  const trimmed = html.trim()
  if (!trimmed) return ''

  const doc = new DOMParser().parseFromString(trimmed, 'text/html')
  const container = doc.createElement('div')

  for (const child of Array.from(doc.body.childNodes)) {
    for (const node of normalizeNode(child, doc)) {
      container.appendChild(node)
    }
  }

  const normalized = container.innerHTML.trim()
  if (!normalized) return ''

  return sanitizeMessage(normalized)
}
